/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect } from "react";
import "./App.scss";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Altair from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import { useLiveAPIContext } from "./contexts/LiveAPIContext";
import MobileRedirect from "./components/mobile-redirect/MobileRedirect";
import SplashScreen from "./components/splash-screen/SplashScreen";
import LoginScreen from "./components/login/LoginScreen";
import WelcomeMessage from "./components/welcome-message/WelcomeMessage";
import { isDesktopDevice } from "./lib/device-detection";
import { AllPermissions } from "./utils/permissions";
import cn from "classnames";
import { LiveClientOptions } from "./types";

// Conditionally import DeviceDebug only in development
const DeviceDebug = process.env.NODE_ENV === 'development' 
  ? require("./components/debug/DeviceDebug").default 
  : null;

// Using ephemeral token-based authentication
// Tokens are fetched from backend which locks sensitive configs (system instructions, model, voice)
// This is more secure than exposing API keys in the frontend
console.log("🔑 [GEMINI] Using ephemeral token-based authentication (secure mode)");
const apiOptions: LiveClientOptions = {
  // Token will be fetched dynamically when connecting
};

// Main app component that handles the LiveAPI session
function MainApp({ 
  userData, 
  permissions, 
  onLogout 
}: { 
  userData: {username: string; mobileNumber: string} | null;
  permissions: AllPermissions | null;
  onLogout: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const { disconnect, stopAudio } = useLiveAPIContext();

  const handleLogout = async () => {
    // Stop audio streaming immediately (first priority)
    stopAudio();
    
    // Stop any active video streams
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    
    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    // Disconnect the LiveAPI session
    await disconnect();
    
    // Then call the parent logout handler
    onLogout();
  };

  return (
    <div className="streaming-console">
      <main>
        <div className="main-app-area">
          {/* APP goes here */}
          <Altair key={userData?.username || 'no-user'} permissions={permissions} userData={userData} />
          <video
            className={cn("stream", {
              hidden: !videoRef.current || !videoStream,
            })}
            ref={videoRef}
            autoPlay
            playsInline
          />
        </div>

        <ControlTray
          videoRef={videoRef}
          supportsVideo={true}
          onVideoStreamChange={setVideoStream}
        >
          {/* Logout button */}
          {userData && (
            <button
              className="action-button logout-button"
              onClick={handleLogout}
              title={`Logout (${userData.username})`}
            >
              <span className="material-symbols-outlined">logout</span>
            </button>
          )}
        </ControlTray>
      </main>
    </div>
  );
}

function App() {
  // Device detection state
  const [showMobileRedirect, setShowMobileRedirect] = useState<boolean>(false);
  // Splash screen state
  const [showSplash, setShowSplash] = useState<boolean>(true);
  // Login screen state
  const [showLogin, setShowLogin] = useState<boolean>(false);
  // Welcome message state
  const [showWelcome, setShowWelcome] = useState<boolean>(false);
  // Permissions state
  const [permissions, setPermissions] = useState<AllPermissions | null>(null);
  // User data state
  const [userData, setUserData] = useState<{username: string; mobileNumber: string} | null>(null);

  // Mobile redirect URL
  const MOBILE_REDIRECT_URL = "https://livechat.aqary.ai/chat";

  useEffect(() => {
    // Check if device is desktop and show mobile redirect
    // Add URL parameter ?bypass=true to skip mobile redirect for testing
    const urlParams = new URLSearchParams(window.location.search);
    const bypassRedirect = urlParams.get('bypass') === 'true';
    
    if (bypassRedirect) {
      setShowMobileRedirect(false);
    } else {
      const isDesktop = isDesktopDevice();
      setShowMobileRedirect(isDesktop);
    }

    // Check if user is already logged in
    const savedUserData = localStorage.getItem('aqary_user_data');
    if (savedUserData) {
      try {
        const parsedUserData = JSON.parse(savedUserData);
        setUserData(parsedUserData);
      } catch (error) {
        console.error('Failed to parse saved user data:', error);
        localStorage.removeItem('aqary_user_data');
      }
    }
  }, []);

  // Handle splash screen completion
  const handleSplashComplete = () => {
    setShowSplash(false);
    // If user is already logged in, skip login screen
    if (userData) {
      setShowWelcome(true);
    } else {
      setShowLogin(true);
    }
  };

  // Handle login completion
  const handleLoginComplete = (loginData: {username: string; mobileNumber: string}) => {
    setUserData(loginData);
    setShowLogin(false);
    setShowWelcome(true);
  };

  // Handle welcome message completion with permissions
  const handleWelcomeComplete = (grantedPermissions: AllPermissions) => {
    setPermissions(grantedPermissions);
    setShowWelcome(false);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('aqary_user_data');
    setUserData(null);
    setPermissions(null);
    setShowLogin(true);
  };

  // Show mobile redirect if on desktop
  if (showMobileRedirect) {
    return (
      <ThemeProvider>
        <div className="App">
          <MobileRedirect 
            redirectUrl={MOBILE_REDIRECT_URL}
            title="Aqary Live Chatbot"
            subtitle="This application is optimized for mobile devices. Please scan the QR code below to access it on your mobile device."
          />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="App">
        {/* Debug component - shows device detection info */}
        {process.env.NODE_ENV === 'development' && DeviceDebug && <DeviceDebug />}
        
        {/* Splash Screen */}
        {showSplash && (
          <SplashScreen onComplete={handleSplashComplete} />
        )}
        
        {/* Login Screen */}
        {showLogin && (
          <LoginScreen onComplete={handleLoginComplete} />
        )}
        
        {/* Welcome Message */}
        {showWelcome && (
          <WelcomeMessage onComplete={handleWelcomeComplete} />
        )}
        
        <LiveAPIProvider key={userData?.username || 'no-user'} options={apiOptions}>
          {(() => {
            console.log("🚀 [GEMINI] LiveAPIProvider initialized with user:", userData?.username || 'no-user');
            return null;
          })()}
          <MainApp 
            userData={userData}
            permissions={permissions}
            onLogout={handleLogout}
          />
        </LiveAPIProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
